import { useState } from 'react';
import { Button, DataGrid, FormDialog, unwrapAction, useGridConfig } from '@eo/shared';
import { Plus } from 'lucide-react';

import type { StaffItem } from './api/types';
import { makeStaffEditFormConfig, staffCreateFormConfig } from './config/formConfig';
import {
  clientOwnerChanges,
  staffToValues,
  staffValuesToCreateBody,
  staffValuesToPatch,
} from './helpers';
import type { NewStaffValues, StaffFormValues, StaffTabProps } from './types';
import { makeStaffGridConfig } from './config/gridConfig';

export function StaffTab({
  staff,
  clients,
  options,
  onQueryOptions,
  pagination,
  onCreate,
  onUpdate,
}: StaffTabProps) {
  const [editing, setEditing] = useState<StaffItem | null>(null);
  const [creating, setCreating] = useState(false);
  const config = useGridConfig(makeStaffGridConfig, { onEdit: setEditing });

  return (
    <section className="flex flex-col gap-4">
      <DataGrid<StaffItem>
        config={config}
        rows={staff}
        pagination={pagination}
        headerActions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            Add staff
          </Button>
        }
      />

      <FormDialog<NewStaffValues>
        open={creating}
        onOpenChange={setCreating}
        title="Add staff member"
        description="They can sign in as soon as the row exists."
        config={staffCreateFormConfig}
        initial={{ email: '', name: '' }}
        validate={(values) => ({
          ...(values.email.trim() ? {} : { email: 'Enter an email.' }),
          ...(values.name.trim() ? {} : { name: 'Enter a display name.' }),
        })}
        onSubmit={async (values) => {
          unwrapAction(
            await onCreate(staffValuesToCreateBody(values)),
            'Adding the staff member failed. Try again.',
          );
        }}
        submitLabel="Add staff"
        pendingLabel="Adding…"
        successToast={{ title: 'Staff member added.' }}
      />

      {editing && (
        <FormDialog<StaffFormValues>
          open
          onOpenChange={(open) => !open && setEditing(null)}
          title={`Edit ${editing.name}`}
          description={editing.email}
          config={makeStaffEditFormConfig({ clients, staff, options, onQueryOptions })}
          initial={staffToValues(editing, clients)}
          validate={(values) => (values.name.trim() ? {} : { name: 'Enter a display name.' })}
          onSubmit={async (values) => {
            unwrapAction(
              await onUpdate({
                id: editing.id,
                patch: staffValuesToPatch(values),
                clientChanges: clientOwnerChanges(
                  staffToValues(editing, clients).clientIds,
                  values.clientIds,
                  editing.id,
                ),
              }),
              'Saving the staff member failed. Try again.',
            );
            setEditing(null);
          }}
          submitLabel="Save changes"
          pendingLabel="Saving…"
          successToast={{ title: 'Staff member updated.' }}
        />
      )}
    </section>
  );
}
